import { useRef, useCallback, useState, useEffect } from 'react';
import { useSitemap } from 'wsm-common-data';
import logger from 'ws-scripts/modules/logger';

import { buildLocationTiles } from '../utils/buildLocationTiles';
import createCustomOverlayClass from '../utils/CustomOverlay';
import { getDealerPhotoBaseUrl } from '../utils/getDealerPhotoBaseUrl';

// Animation timing constant to match CustomOverlay
const OVERLAY_CLOSE_ANIMATION_MS = 150;

/**
 * Custom hook to manage custom overlay for map markers
 * @param {Object} options - Configuration options
 * @param {Object} options.map - The Google Map instance
 * @param {boolean} options.isDragging - Whether the map is being dragged
 * @param {Array} options.locations - Array of locations (triggers overlay close on change for pagination)
 * @returns {Object} - customOverlayRef and handleMarkerClick function
 */
const useCustomOverlay = ({ map, isDragging, locations }) => {
	const customOverlayRef = useRef(null);
	const [portalContainer, setPortalContainer] = useState(null);
	const [tileData, setTileData] = useState(null);
	const pendingTimeoutRef = useRef(null);
	const customOverlayClassRef = useRef(null);

	// Use a ref so handleMarkerClick doesn't need isDragging in its deps.
	// This prevents the markers effect from re-running on every drag,
	// which was destroying/recreating markers and snapping the map back.
	const isDraggingRef = useRef(isDragging);
	isDraggingRef.current = isDragging;

	const sitemap = useSitemap();
	const rawLocationDetailPageUrl = sitemap.get('LOCATION_DETAILS');
	const locationDetailPagePart = (rawLocationDetailPageUrl || '').slice(
		0,
		-4
	);

	/**
	 * Helper function to create and show overlay
	 * @param {Object} location - Location data object
	 * @param {Object} marker - Google Maps marker instance
	 */
	const openOverlay = useCallback(
		(location, marker) => {
			if (!location || !marker) {
				logger.warn(
					'Invalid location or marker provided to openOverlay'
				);
				return;
			}

			// Validate location has some identifiable data
			// Location object can have various structures depending on data source
			// Check for truthy values (excludes empty strings, null, undefined)
			const hasMinimalData =
				location &&
				(Boolean(location.name && location.name.trim()) ||
					Boolean(location.city && location.city.trim()) ||
					Boolean(location.id));

			if (!hasMinimalData) {
				logger.warn('Location missing required data fields', {
					location
				});
				return;
			}
			try {
				const builtTileData = buildLocationTiles(
					[location],
					locationDetailPagePart,
					getDealerPhotoBaseUrl()
				);

				if (
					!builtTileData ||
					!builtTileData.length ||
					!builtTileData[0]
				) {
					logger.warn('buildLocationTiles returned empty data', {
						location
					});
					return;
				}

				const markerPosition = marker.getPosition();
				if (!markerPosition) {
					logger.warn('Marker has no position');
					return;
				}

				// Extract icon for dynamic pin sizing
				const markerIcon = marker.getIcon();
				if (!markerIcon) {
					logger.debug(
						'Marker icon not found, will use default pin dimensions'
					);
				}

				// Create the CustomOverlay class only once
				if (!customOverlayClassRef.current) {
					customOverlayClassRef.current = createCustomOverlayClass();
				}

				const CustomOverlay = customOverlayClassRef.current;
				const newOverlay = new CustomOverlay(
					markerPosition,
					() => {
						// Only clear ref if it still points to this specific overlay instance
						if (customOverlayRef.current === newOverlay) {
							// Remove overlay from map before clearing ref
							if (customOverlayRef.current.setMap) {
								customOverlayRef.current.setMap(null);
							}
							customOverlayRef.current = null;
							setPortalContainer(null);
							setTileData(null);
						}
					},
					(containerDiv) => {
						// Set the container when it's ready
						setPortalContainer(containerDiv);
					},
					markerIcon
				);

				// Validate overlay was created successfully
				if (!newOverlay) {
					logger.error('Failed to create CustomOverlay instance');
					return;
				}

				// Set the overlay reference and add to map
				customOverlayRef.current = newOverlay;

				// Store the tile data for the portal
				setTileData(builtTileData);

				// Ensure map ref is still valid
				if (map) {
					newOverlay.setMap(map);
				} else {
					logger.warn(
						'Map ref is not available when setting overlay'
					);
				}
			} catch (error) {
				logger.error('Error creating overlay:', error);
				customOverlayRef.current = null;
				setPortalContainer(null);
				setTileData(null);
			}
		},
		[map, locationDetailPagePart]
	);

	/**
	 * Handle marker click to show custom overlay
	 * @param {Object} location - Location data object
	 * @param {Object} marker - Google Maps marker instance
	 */
	const handleMarkerClick = useCallback(
		(location, marker) => {
			if (isDraggingRef.current) {
				return;
			}

			// Cancel any pending operations to prevent race conditions
			if (pendingTimeoutRef.current) {
				clearTimeout(pendingTimeoutRef.current);
				pendingTimeoutRef.current = null;
			}

			// Close existing overlay with animation
			if (customOverlayRef.current) {
				if (typeof customOverlayRef.current.close === 'function') {
					customOverlayRef.current.close();
				}
				// Wait for close animation to complete before opening new overlay
				pendingTimeoutRef.current = setTimeout(() => {
					if (
						customOverlayRef.current === null ||
						customOverlayRef.current.isClosing
					) {
						openOverlay(location, marker);
					}
					pendingTimeoutRef.current = null;
				}, OVERLAY_CLOSE_ANIMATION_MS);
			} else {
				// No existing overlay, open immediately
				openOverlay(location, marker);
			}
		},
		[openOverlay]
	);

	// Close overlay when locations change (e.g., pagination)
	// Also cancel any pending overlay operations to prevent stale overlays
	useEffect(() => {
		if (pendingTimeoutRef.current) {
			clearTimeout(pendingTimeoutRef.current);
			pendingTimeoutRef.current = null;
		}
		customOverlayRef.current?.close?.();
	}, [locations]);

	// Cleanup pending timeouts on unmount
	useEffect(() => {
		return () => {
			if (pendingTimeoutRef.current) {
				clearTimeout(pendingTimeoutRef.current);
			}
		};
	}, []);

	return {
		customOverlayRef,
		handleMarkerClick,
		portalContainer,
		tileData
	};
};

export default useCustomOverlay;
